import { useCallback, useEffect, useState } from 'react';
import { useAtom } from 'jotai';

import { authenticationAtom } from '../State';

import { authenticatedFetch } from '../JWTAuthService';
import { configuration } from '../Configuration';
import { ParcelBrw } from '../heatmap/brwScale';
import { PerspectiveScope } from './sitePerspectiveApi';

/**
 * The parcels the site estimator assigned to the plot.
 *
 * One read for the phase in view, shared by the table in the cadastral drill-down and the outlines
 * on the small map beside the property — so what is listed and what is drawn cannot disagree.
 */

const baseUrl = configuration.serverUrl;

export type ParcelRole = 'core' | 'manual' | 'accepted' | 'open' | 'rejected';

/**
 * What a person said about a parcel. `estimated` is not a third state but the withdrawal of a
 * decision: it forgets what was said and hands the parcel back to the estimator's answer.
 */
export type ParcelDecision = 'assigned' | 'removed' | 'estimated';

export interface PhaseParcelRow {
    parcelId: string;
    /** Flurstücksnummer, "3124/1"; null where the state's extract carries none. */
    number: string | null;
    gemarkung: string | null;
    flur: string | null;
    areaM2: number;
    role: ParcelRole;
    /** Null for a parcel the building stands on and for one a person assigned: neither was scored. */
    score: number | null;
    reasons: string[];
    geometry: any;
    /** Der Bodenrichtwert, wie er am Flurstück steht — vom Server durchgereicht, nicht gerechnet. */
    brw?: ParcelBrw | null;
}

export interface PhaseParcelResult {
    rows: PhaseParcelRow[];
    /** Area of what is certain, and of what is possible on top of it. */
    siteAreaMin: number | null;
    siteAreaMax: number | null;
    /**
     * The perspectives the plot moved, as they now stand. Only a write returns them.
     *
     * The plot area is `landArea` on the cadastral perspective and the divisor of both ratios on
     * the zoning one, so a click on a parcel changes figures on two tiles the reader can see at the
     * same time. Sending them back with the plot is what keeps the three in step; leaving the page
     * to re-fetch would show the old numbers for as long as the request took, which is exactly long
     * enough to be read.
     */
    perspectives?: Record<string, any>;
}

export async function getPhaseParcels(scope: PerspectiveScope): Promise<PhaseParcelResult> {
    const parameters = new URLSearchParams();
    Object.entries(scope).forEach(([key, value]) => {
        if (value) parameters.set(key, String(value));
    });

    const response = await authenticatedFetch(`${baseUrl}/site-phases/parcels?${parameters.toString()}`, {
        method: 'GET',
    });

    if (!response || !response.ok) {
        throw new Error(`Flurstücke konnten nicht geladen werden (${response?.status ?? 'keine Antwort'})`);
    }

    const payload = await response.json();
    return {
        rows: Array.isArray(payload?.rows) ? payload.rows : [],
        siteAreaMin: payload?.siteAreaMin ?? null,
        siteAreaMax: payload?.siteAreaMax ?? null,
    };
}

/**
 * Record a decision about one parcel and get the whole plot back.
 *
 * The whole plot, because a decision moves the outlines *and* the area, and both come from one rule
 * on the server. A client that patched its own list would be a second implementation of that rule,
 * and the two would part company the first time either changed.
 */
export async function setParcelDecision(
    scope: PerspectiveScope,
    parcelId: string,
    decision: ParcelDecision,
): Promise<PhaseParcelResult> {
    const response = await authenticatedFetch(`${baseUrl}/site-phases/parcels`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...scope, parcelId, decision }),
    });

    if (!response || !response.ok) {
        throw new Error(`Die Zuordnung konnte nicht gespeichert werden (${response?.status ?? 'keine Antwort'})`);
    }

    // What was just written is the current answer; the cached read is not.
    invalidatePhaseParcels();

    const payload = await response.json();
    return {
        rows: Array.isArray(payload?.rows) ? payload.rows : [],
        siteAreaMin: payload?.siteAreaMin ?? null,
        siteAreaMax: payload?.siteAreaMax ?? null,
        perspectives: payload?.perspectives ?? undefined,
    };
}

export interface PhaseParcelState extends PhaseParcelResult {
    loading: boolean;
    error: string | null;
    /**
     * Takes a plot the server just returned and makes it the one on screen.
     *
     * On the state rather than passed down as a callback, because everything that draws the plot
     * already holds this object: a decision made in the dialog reaches the small map beside the
     * property without a prop for it, and there is no arrangement of components in which the two
     * can be showing different plots.
     */
    apply: (result: PhaseParcelResult) => void;
}

/**
 * One request per phase, however many components ask.
 *
 * The table in the drill-down and the outlines on the small map are two views of one answer, and
 * they have to be the same answer — two independent loads can disagree while one is still in
 * flight, or one can fail while the other succeeds, which on a map means an outline with no row
 * behind it. Both await the same promise instead.
 *
 * Kept briefly: a phase that is re-activated derives new parcels, so a cache that outlived the run
 * would show the previous plot. `invalidatePhaseParcels` is what the page calls when it knows
 * something changed; the age below is the backstop for everything it does not know about.
 *
 * The key carries the tenant. An estimate belongs to one customer — two of them can hold different
 * views of the same address — and a logout and login happen inside the page, without a reload that
 * would clear this. Without the tenant in the key the next session could be served the previous
 * one's plot from memory, without ever asking the server that would have refused it.
 */
const CACHE_MAX_AGE_MS = 30_000;
const cache = new Map<string, { at: number; request: Promise<PhaseParcelResult> }>();

const cachedParcels = (key: string, scope: PerspectiveScope): Promise<PhaseParcelResult> => {
    const known = cache.get(key);
    if (known && Date.now() - known.at < CACHE_MAX_AGE_MS) return known.request;

    const request = getPhaseParcels(scope).catch((error) => {
        // A failure must not be remembered: the next render would inherit it without ever asking
        // again.
        cache.delete(key);
        throw error;
    });

    cache.set(key, { at: Date.now(), request });
    return request;
};

export const invalidatePhaseParcels = (): void => cache.clear();

const EMPTY = { rows: [], siteAreaMin: null, siteAreaMax: null, loading: false, error: null };

export const usePhaseParcels = (scope: PerspectiveScope | null): PhaseParcelState => {
    const [authentication] = useAtom(authenticationAtom) as any;
    const [state, setState] = useState<Omit<PhaseParcelState, 'apply'>>(EMPTY);

    // The scope object is rebuilt on every render of the page; its contents are what matter — and
    // the tenant, because the same address means different things to different customers.
    const key = `${authentication?.customer ?? ''}|${JSON.stringify(scope ?? {})}`;

    useEffect(() => {
        if (!scope) {
            setState(EMPTY);
            return;
        }

        let cancelled = false;
        setState((current) => ({ ...current, loading: true, error: null }));

        cachedParcels(key, scope)
            .then((result) => {
                if (!cancelled) setState({ ...result, loading: false, error: null });
            })
            .catch((error: any) => {
                console.error('Could not load the parcels of the plot', error);
                if (!cancelled) setState({ ...EMPTY, error: error?.message ?? 'Fehler' });
            });

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [key]);

    const apply = useCallback(
        (result: PhaseParcelResult) => setState({ ...result, loading: false, error: null }),
        [],
    );

    return { ...state, apply };
};
